import { ComponentType } from 'react'
import { useSignals } from '@preact/signals-react/runtime'
import { Signal } from '@preact/signals-react'
import { unwrapValue } from '../../utils/signal.utils'
import { Logger, LogLevel } from '../../core/Logger'

/**
 * Higher-Order Component that automatically unwraps Signal props before passing them to the wrapped component.
 *
 * This allows block components to receive props that can be either direct values (e.g., string)
 * or Signals (e.g., Signal<string>), with automatic unwrapping so components always receive
 * unwrapped values.
 *
 * The HOC also calls useSignals() to ensure React's signal reactivity works correctly,
 * causing components to re-render when Signal values change.
 *
 * If unwrapping a signal fails, the error is logged and the original value is used as a fallback.
 *
 * @param Component - The component to wrap
 * @returns A new component that unwraps Signal props before passing them to the original component
 */
export function withUnwrappedSignals<P extends object>(
    Component: ComponentType<P>
): ComponentType<P> {
    return (props: P) => {
        useSignals()

        const unwrappedProps = {} as P

        Object.keys(props).forEach(key => {
            const value = props[key as keyof P]
            try {
                unwrappedProps[key as keyof P] = unwrapValue(
                    value as Signal<unknown> | unknown
                ) as P[keyof P]
            } catch (error) {
                const logger = Logger.getInstance()
                logger.log(LogLevel.ERROR, `Failed to unwrap signal for prop "${key}"`, {
                    source: 'withUnwrappedSignals',
                    error: error instanceof Error ? error : String(error),
                    context: {
                        propKey: key,
                        blockId: (props as Record<string, unknown>)['blockId'],
                        hint: 'A state variable may contain an invalid or incompatible value.',
                    },
                })
                unwrappedProps[key as keyof P] = value
            }
        })

        return <Component {...unwrappedProps} />
    }
}
